import { z } from 'zod';
import { eventSchema } from './envelope';

export const REACH_POST_SEEN_V1 = 'reach.post.seen.v1' as const;
export const REACH_POST_ACKNOWLEDGED_V1 = 'reach.post.acknowledged.v1' as const;
export const REACH_REMINDER_REQUESTED_V1 = 'reach.reminder.requested.v1' as const;
export const REACH_ESCALATION_RAISED_V1 = 'reach.escalation.raised.v1' as const;

/**
 * Er is gekeken, geteld per minuut en niet per persoon.
 *
 * Met opzet geaggregeerd (ADR-0014): een event per persoon zou een logboek zijn van wie wanneer
 * wat las, en dat is precies het soort gegeven dat hier niet hoort te bestaan. Wat een monitor
 * moet weten, is dat een bericht aankomt — niet bij wie.
 */
export const postSeenV1Schema = eventSchema({
  postId: z.guid(),
  /** Hoeveel mensen het bericht in deze minuut voor het eerst openden. */
  count: z.number().int(),
  minute: z.string().datetime(),
});

/**
 * Iemand heeft bevestigd. Wél per persoon: dit is bewijs, en bewijs zonder persoon is niets.
 *
 * Hier hangt straks de escalatie aan (Stap 17) en de rapportage over tijd (Stap 33).
 */
export const postAcknowledgedV1Schema = eventSchema({
  postId: z.guid(),
  personId: z.guid(),
  postVersion: z.number().int(),
  languageShown: z.string(),
});

/**
 * Communicatie vraagt om een herinnering.
 *
 * `reach` stuurt zelf niets: het zegt wie er een herinnering hoort te krijgen, en
 * `notifications` beslist langs welk kanaal dat gaat. Zonder dit event zou `reach` pushmeldingen
 * moeten kennen, en dan zitten twee modules aan hetzelfde touwtje.
 */
export const reminderRequestedV1Schema = eventSchema({
  postId: z.guid(),
  title: z.string(),
  /** De taal van de titel hierboven; `notifications` vertaalt hem daaruit (Stap 11). */
  sourceLanguage: z.string().default('nl'),
  target: z.enum(['not_seen', 'not_acknowledged']),
  personIds: z.array(z.guid()),
  /** Volgens het herinneringsschema, niet door iemand op de knop (Stap 16). */
  automatic: z.boolean().default(false),
});

/**
 * De termijn is voorbij en er staat nog iets open in het team van deze leidinggevende (Stap 17).
 *
 * Eén event per leidinggevende per bericht, en dat "één keer" is al beslist vóór dit event
 * bestaat: `reach` claimt de rij in `reach.escalation` en publiceert alleen als die claim lukte.
 * Zou de ontdubbeling in `notifications` zitten, dan zou elke kwartierronde een melding meer
 * opleveren bij een worker die een keer herstart.
 *
 * `names` is leeg wanneer de namen-poort dicht is (ADR-0014); het aantal staat er altijd in
 * (ADR-0049). Deze module beslist dat niet - ze schrijft op wat ze mag doorgeven.
 */
export const escalationRaisedV1Schema = eventSchema({
  postId: z.guid(),
  title: z.string(),
  sourceLanguage: z.string().default('nl'),
  managerPersonId: z.guid(),
  /** Hoeveel van zijn mensen het bericht nog niet bevestigden. */
  openCount: z.number().int(),
  /** De namen, of leeg als ze niet mogen. */
  names: z.array(z.string()).default([]),
});

export type PostSeenV1 = z.infer<typeof postSeenV1Schema>;
export type PostAcknowledgedV1 = z.infer<typeof postAcknowledgedV1Schema>;
export type ReminderRequestedV1 = z.infer<typeof reminderRequestedV1Schema>;
export type EscalationRaisedV1 = z.infer<typeof escalationRaisedV1Schema>;

export const reachEventCatalog = {
  [REACH_POST_SEEN_V1]: postSeenV1Schema,
  [REACH_POST_ACKNOWLEDGED_V1]: postAcknowledgedV1Schema,
  [REACH_REMINDER_REQUESTED_V1]: reminderRequestedV1Schema,
  [REACH_ESCALATION_RAISED_V1]: escalationRaisedV1Schema,
} as const;

// --- Beheerdershandelingen voor het auditspoor (Stap 18) ------------------------------------

export const REACH_PAPER_RECORDED_V1 = 'reach.paper.recorded.v1' as const;
export const REACH_EVIDENCE_EXPORTED_V1 = 'reach.evidence.exported.v1' as const;

/** Papieren bevestigingen geregistreerd (Stap 42a): door wie, voor welk bericht, hoeveel. */
export const paperRecordedV1Schema = eventSchema({
  postId: z.guid(),
  personIds: z.array(z.guid()),
  signedOn: z.string().date(),
  recorded: z.number().int(),
});
export type PaperRecordedV1 = z.infer<typeof paperRecordedV1Schema>;

/** Het bewijs van een bericht is geëxporteerd (CSV). Een export is een handeling die telt. */
export const evidenceExportedV1Schema = eventSchema({
  postId: z.guid(),
  rows: z.number().int(),
});
export type EvidenceExportedV1 = z.infer<typeof evidenceExportedV1Schema>;
